/**
 * documentIncidents.ts — Incidencias del documento cargado (INFDOC-1)
 *
 * Detector común del que saldrán la pastilla de P1 (`INFDOC-2`), los dos PDF
 * (`INFDOC-3`) y los dos ODT (`INFDOC-4`): el «Informe para el ayuntamiento» y
 * el «Anexo técnico» del GREA (DOP-1 de INFDOC-0). Sustituirá a
 * `errorDetector.ts` en `INFDOC-5`, que no se toca aquí.
 *
 * DOP de INFDOC-1 (Luis, 2-nov-2026): fichero nuevo (1 a); una función sobre
 * las filas FINALES de P1 (2 a; P1 enseña «…» hasta el final, DOP-4 de
 * INFDOC-0); la contención de la coordenada del DOCUMENTO se mide con el
 * validador de límites, porque la fila de P1 solo trae la de la coordenada
 * final (3 b); lista cerrada de códigos con clase y destino (4 a).
 *
 * Reglas medidas en `.ptel/p0_infdoc_20261101/analiza_inventario.py`
 * (46 documentos, 2 108 filas). Límites de la DOP-3 de INFDOC-0: nada que
 * dependa de una limitación de la app (solo «documento lejos» cuando DOS
 * fuentes distintas coinciden entre sí) y ED50 inferido solo para el GREA.
 *
 * Inerte en este commit: sin consumidor en pantalla ni en exportación.
 *
 * @module services/analysis/documentIncidents
 */

import type { ProcessedInfrastructure } from '../../types/processing';
import { esCentroideMunicipal, esOrigenCentroide } from '../../utils/procedenciaVia';
import { validateWithinMunicipality } from '../validation/MunicipalBoundaryValidator';

// ═════════════════════════════════════════════════════════════════════════════
// LISTA CERRADA DE CÓDIGOS (DOP-4 a)
// ═════════════════════════════════════════════════════════════════════════════

/** comprobada = defecto demostrable sin fuentes; por_confirmar = sospecha; solo_grea = de la lectura de la app */
export type ClaseIncidencia = 'comprobada' | 'por_confirmar' | 'solo_grea';
/** A quién va: el informe para el ayuntamiento o solo el anexo del GREA */
export type DestinoIncidencia = 'ayuntamiento' | 'grea';

export const FICHA_INCIDENCIA = {
  sin_coordenada: { clase: 'comprobada', destino: 'ayuntamiento' },
  sin_coordenada_ni_direccion: { clase: 'comprobada', destino: 'ayuntamiento' },
  coordenada_incompleta: { clase: 'comprobada', destino: 'ayuntamiento' },
  coordenada_mal_escrita: { clase: 'comprobada', destino: 'ayuntamiento' },
  rotulo_de_plantilla_en_coordenada: { clase: 'comprobada', destino: 'ayuntamiento' },
  fuera_del_termino_mas_de_500_m: { clase: 'comprobada', destino: 'ayuntamiento' },
  fuera_del_termino_hasta_500_m: { clase: 'comprobada', destino: 'ayuntamiento' },
  nombre_generico: { clase: 'comprobada', destino: 'ayuntamiento' },
  siglas_solas: { clase: 'comprobada', destino: 'ayuntamiento' },
  texto_mal_codificado: { clase: 'comprobada', destino: 'ayuntamiento' },
  palabras_pegadas: { clase: 'comprobada', destino: 'ayuntamiento' },
  coordenada_compartida: { clase: 'por_confirmar', destino: 'ayuntamiento' },
  sin_coordenada_y_sin_localizar: { clase: 'por_confirmar', destino: 'ayuntamiento' },
  documento_lejos_de_fuentes_que_coinciden: { clase: 'por_confirmar', destino: 'ayuntamiento' },
  sistema_de_referencia_antiguo_inferido: { clase: 'por_confirmar', destino: 'grea' },
  fila_repetida: { clase: 'solo_grea', destino: 'grea' },
  documento_sin_municipio: { clase: 'comprobada', destino: 'grea' },
} as const satisfies Record<string, { clase: ClaseIncidencia; destino: DestinoIncidencia }>;

export type CodigoIncidencia = keyof typeof FICHA_INCIDENCIA;
export const CODIGOS_INCIDENCIA = Object.keys(FICHA_INCIDENCIA) as CodigoIncidencia[];

// ═════════════════════════════════════════════════════════════════════════════
// TIPOS PÚBLICOS
// ═════════════════════════════════════════════════════════════════════════════

/** Contención de la coordenada del DOCUMENTO en su término (tolerancia 50 m, como la puerta del pipeline) */
export type ContencionDocumento =
  | { estado: 'medida'; fuera: boolean; distanciaMetros: number }
  /** Sin límite cargado o con fallo: no se sabe; nunca se da por «dentro» */
  | { estado: 'sin_validar'; motivo: string };

export interface IncidenciaDocumento {
  codigo: CodigoIncidencia;
  /** `id` de la fila; `null` en las del documento (`documento_sin_municipio`) */
  filaId: string | null;
  /** Posición de la fila en la lista de P1 (0 = primera); `null` en las del documento */
  indice: number | null;
  /** Distancia al límite del término (fuera del término) o al punto medio de las dos fuentes (documento lejos) */
  distanciaMetros?: number;
  /** Las dos fuentes distintas que coinciden entre sí (documento lejos) */
  fuentes?: [string, string];
  /** `id` de las filas que comparten la coordenada (compartida) o de la fila que se repite (repetida) */
  conFilas?: string[];
}

export interface InformeIncidenciasDocumento {
  incidencias: IncidenciaDocumento[];
  /** Recuento por código; todos los códigos están, con 0 si no hay */
  porCodigo: Record<CodigoIncidencia, number>;
  totalFilas: number;
  /** Filas distintas con al menos una incidencia */
  filasConIncidencia: number;
  /** Filas con coordenada del documento cuya contención no se pudo medir */
  filasSinValidarLimite: number;
}

export interface ContextoDetector {
  /** INE de 5 dígitos del documento; sin él, `documento_sin_municipio` */
  ineCode?: string;
  /** Contención de la coordenada del documento por `id` de fila (`medirContencionDelDocumento`) */
  contencion?: ReadonlyMap<string, ContencionDocumento>;
}

// ═════════════════════════════════════════════════════════════════════════════
// REGLAS (las de `analiza_inventario.py`, INFDOC-0)
// ═════════════════════════════════════════════════════════════════════════════

/** = `errorDetector.isGenericName` (ERR-5), sin el caso «nombre vacío» */
const GENERICO = [
  /^(colegio|instituto|escuela|centro de salud|hospital|ambulatorio|consultorio)$/,
  /^(polideportivo|pabellón|campo de fútbol|piscina)$/,
  /^(ayuntamiento|casa consistorial|juzgado)$/,
  /^(iglesia|ermita|parroquia)$/,
  /^(cuartel|comisaría|policía local)$/,
  /^(parque de bomberos|bomberos)$/,
  /^(s\/n|sin nombre|nd|n\/a|\?+|-+)$/,
];
/** Siglas de depuradora o potabilizadora solas (FILC4, DOP-3) */
const SIGLAS = /^(e\.?d\.?a\.?r\.?|e\.?t\.?a\.?p\.?|e\.?b\.?a\.?r\.?|edari|etar)$/i;
/** Texto leído con otra codificación (UTF-8 como Latin-1, carácter de sustitución, «?» en medio de palabra) */
const MOJIBAKE = /\u00C3[\u0080-\u00BF]|\u00C2[\u00A0-\u00BF]|\uFFFD|\u00E2\u20AC|[A-Za-z]\?[a-z]/;
/** Mayúscula en medio de una palabra en minúsculas («Extinciónde» no; «PolÍgono» sí) */
const PEGADAS = /[a-záéíóúñ]{2}[A-ZÁÉÍÓÚÑ][a-záéíóúñ]/;
/** Rótulo interno de una plantilla en la celda de la coordenada («AVI_LA_TBL_COL_COO_LON») */
const ROTULO = /[A-Z]{2,}_[A-Z_]{3,}/;
const UMBRAL_FUERA_M = 500;
const COINCIDEN_M = 150;
const LEJOS_M = 500;

// ═════════════════════════════════════════════════════════════════════════════
// AYUDAS
// ═════════════════════════════════════════════════════════════════════════════

const esNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Coordenada que escribió el DOCUMENTO, ya normalizada en P1 (con la corrección
 * ED50 si la hubo). `normalization` sobrevive a la geolocalización (`...infra`
 * en `useAutoGeocoding`), aunque `xFinal/yFinal` pasen a ser la referencia.
 */
export function coordenadaDelDocumento(fila: ProcessedInfrastructure): { x: number; y: number } | null {
  const n = fila.normalization;
  return n && esNumero(n.x) && esNumero(n.y) ? { x: n.x, y: n.y } : null;
}

/** Nombre sin tildes ni signos, para ver si dos filas con el mismo punto son la misma */
function nombreComparable(s: string | undefined): string {
  return (s ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function esGenerico(nombre: string): boolean {
  const n = nombre.trim().toLowerCase();
  return GENERICO.some(p => p.test(n));
}

/** La app intentó situarla y se quedó sin punto o en el centroide del municipio */
function sinLocalizar(fila: ProcessedInfrastructure): boolean {
  if (!fila.geocoding) return false;
  if (!esNumero(fila.xFinal) || !esNumero(fila.yFinal)) return true;
  return esOrigenCentroide(fila.source) || esCentroideMunicipal(fila.geocoding.geocoderUsed, fila.source);
}

/**
 * DOP-3 (límite 1) de INFDOC-0: dos fuentes DISTINTAS (familia antes de «:») que
 * coinciden entre sí a ≤ 150 m; con una sola fuente, o con fuentes que no
 * coinciden, no hay incidencia (sería una limitación de la app, caso EDAR-b).
 */
function dosFuentesQueCoinciden(fila: ProcessedInfrastructure): { a: string; b: string; x: number; y: number } | null {
  const fx = (fila.geocoding?.sourcesConsulted ?? []).filter(f => esNumero(f.x) && esNumero(f.y));
  for (let i = 0; i < fx.length; i++) {
    for (let j = i + 1; j < fx.length; j++) {
      const a = fx[i], b = fx[j];
      if (a.source.split(':')[0] === b.source.split(':')[0]) continue;
      if (Math.hypot(a.x - b.x, a.y - b.y) <= COINCIDEN_M) {
        return { a: a.source, b: b.source, x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      }
    }
  }
  return null;
}

// ═════════════════════════════════════════════════════════════════════════════
// DETECTOR (síncrono y puro)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Incidencias del documento sobre las filas FINALES de P1 (tras la geolocalización).
 * Una fila puede llevar varios códigos; el informe las agrupará por fila.
 *
 * @param filas - `processedData.infrastructures` de P1, en su orden
 * @param ctx.ineCode - INE del documento (sin él: `documento_sin_municipio`)
 * @param ctx.contencion - medida con `medirContencionDelDocumento`; sin ella no
 *        salen las de «fuera del término» y esas filas cuentan como sin validar
 */
export function detectarIncidenciasDocumento(
  filas: readonly ProcessedInfrastructure[],
  ctx: ContextoDetector = {},
): InformeIncidenciasDocumento {
  const incidencias: IncidenciaDocumento[] = [];
  const anota = (codigo: CodigoIncidencia, fila: ProcessedInfrastructure | null, indice: number | null,
    extra: Partial<IncidenciaDocumento> = {}) =>
    incidencias.push({ codigo, filaId: fila?.id ?? null, indice, ...extra });

  if (!ctx.ineCode && filas.length > 0) anota('documento_sin_municipio', null, null);

  let sinValidar = 0;
  const grupos = new Map<string, number[]>();
  filas.forEach((fila, i) => {
    const xo = (fila.xOriginal ?? '').trim();
    const yo = (fila.yOriginal ?? '').trim();
    const doc = coordenadaDelDocumento(fila);
    if (ROTULO.test(xo) || ROTULO.test(yo)) anota('rotulo_de_plantilla_en_coordenada', fila, i);
    else if (!xo && !yo) {
      anota('sin_coordenada', fila, i);
      if (!(fila.direccion ?? '').trim()) anota('sin_coordenada_ni_direccion', fila, i);
    } else if (!xo || !yo) anota('coordenada_incompleta', fila, i);
    else if (!doc) anota('coordenada_mal_escrita', fila, i);

    if (doc) {
      const c = ctx.contencion?.get(fila.id);
      if (!c || c.estado === 'sin_validar') sinValidar++;
      else if (c.fuera) {
        anota(c.distanciaMetros > UMBRAL_FUERA_M ? 'fuera_del_termino_mas_de_500_m' : 'fuera_del_termino_hasta_500_m',
          fila, i, { distanciaMetros: c.distanciaMetros });
      }
      const clave = `${Math.round(doc.x)}|${Math.round(doc.y)}`;
      grupos.set(clave, [...(grupos.get(clave) ?? []), i]);
    }
    if ((fila.normalization?.warnings ?? []).some(a => a.includes('ED50'))) {
      anota('sistema_de_referencia_antiguo_inferido', fila, i);
    }

    const nombre = fila.nombre ?? '';
    if (esGenerico(nombre)) anota('nombre_generico', fila, i);
    if (SIGLAS.test(nombre.trim())) anota('siglas_solas', fila, i);
    if (MOJIBAKE.test(nombre) || MOJIBAKE.test(fila.direccion ?? '')) anota('texto_mal_codificado', fila, i);
    if (PEGADAS.test(nombre)) anota('palabras_pegadas', fila, i);

    if (doc && fila.crossValidation?.flag === 'REVISION_MANUAL') {
      const par = dosFuentesQueCoinciden(fila);
      const d = par ? Math.hypot(doc.x - par.x, doc.y - par.y) : 0;
      if (par && d > LEJOS_M) {
        anota('documento_lejos_de_fuentes_que_coinciden', fila, i,
          { distanciaMetros: Math.round(d), fuentes: [par.a, par.b] });
      }
    }
    if (!doc && sinLocalizar(fila)) anota('sin_coordenada_y_sin_localizar', fila, i);
  });

  // Misma coordenada del documento: si el nombre también es el mismo, es la fila
  // repetida (solo GREA; la primera no se marca); si no, una pregunta para cada una.
  for (const indices of grupos.values()) {
    if (indices.length < 2) continue;
    const ids = indices.map(i => filas[i].id);
    const nombres = new Set(indices.map(i => nombreComparable(filas[i].nombre)));
    if (nombres.size === 1) {
      for (const i of indices.slice(1)) anota('fila_repetida', filas[i], i, { conFilas: [ids[0]] });
    } else {
      for (const i of indices) {
        anota('coordenada_compartida', filas[i], i, { conFilas: ids.filter(id => id !== filas[i].id) });
      }
    }
  }

  const porCodigo = Object.fromEntries(CODIGOS_INCIDENCIA.map(c => [c, 0])) as Record<CodigoIncidencia, number>;
  for (const inc of incidencias) porCodigo[inc.codigo]++;
  return {
    incidencias,
    porCodigo,
    totalFilas: filas.length,
    filasConIncidencia: new Set(incidencias.filter(x => x.filaId !== null).map(x => x.filaId)).size,
    filasSinValidarLimite: sinValidar,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// CONTENCIÓN DE LA COORDENADA DEL DOCUMENTO (asíncrona, DOP-3 b)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Mide si la coordenada que escribió el documento cae en su término, con el
 * mismo validador y la misma tolerancia (50 m) que la puerta del pipeline. El
 * límite se carga una vez por municipio (caché de VALMUNI). Sin límite, o si la
 * medida falla, la fila queda «sin validar»: nunca se da por dentro.
 */
export async function medirContencionDelDocumento(
  filas: readonly ProcessedInfrastructure[],
  ineCode: string,
): Promise<Map<string, ContencionDocumento>> {
  const salida = new Map<string, ContencionDocumento>();
  for (const fila of filas) {
    const doc = coordenadaDelDocumento(fila);
    if (!doc) continue;
    try {
      const r = await validateWithinMunicipality(doc.x, doc.y, ineCode, { toleranceMeters: 50 });
      salida.set(fila.id, r.sinLimite
        ? { estado: 'sin_validar', motivo: r.sinLimite }
        : { estado: 'medida', fuera: !r.isValid, distanciaMetros: Math.round(r.distanceToBoundary) });
    } catch (e) {
      salida.set(fila.id, { estado: 'sin_validar', motivo: `error: ${(e as Error)?.message ?? String(e)}` });
    }
  }
  return salida;
}
